<template>
  <div v-if="show" class="modal-overlay" @click.self="close">
    <div class="modal-container modal-large"> <!-- Added modal-large -->
      <div class="modal-header">
        <h3>Manage Entities</h3>
        <button @click="close" class="modal-close">&times;</button>
      </div>

      <div class="modal-body">
        <div v-if="error" class="error-message mb-3">{{ error }}</div>

        <!-- Section for Creating/Editing Entities -->
        <div class="entity-form-section card mb-4">
           <div class="card-header">
              <h4>{{ editingEntity ? 'Edit Entity' : 'Create New Entity' }}</h4>
           </div>
           <div class="card-body">
              <!-- ADDED Entity Form -->
              <form @submit.prevent="saveEntity">
                <div class="row">
                  <div class="col-md-6 form-group">
                    <label for="entity-name">Entity Name *</label>
                    <input type="text" id="entity-name" v-model="entityFormData.name" class="form-control" required>
                  </div>
                  <div class="col-md-6 form-group">
                    <label for="entity-type">Entity Type *</label>
                    <select id="entity-type" v-model="entityFormData.entity_type" class="form-control" required>
                      <option value="COMPANY">Company</option>
                      <option value="AGENCY">Insurance Agency/Broker</option>
                      <option value="PERSON">Individual</option>
                      <option value="OTHER">Other</option>
                    </select>
                  </div>
                </div>
                <div class="row">
                  <div class="col-md-6 form-group">
                    <label for="entity-email">Primary Email</label>
                    <input type="email" id="entity-email" v-model="entityFormData.primary_email" class="form-control">
                  </div>
                  <div class="col-md-6 form-group">
                    <label for="entity-phone">Primary Phone</label>
                    <input type="tel" id="entity-phone" v-model="entityFormData.primary_phone" class="form-control">
                  </div>
                </div>
                <AddressFields v-model="entityFormData" prefix="mgr-entity" />

                <button type="submit" class="btn btn-primary" :disabled="isSaving">
                   <span v-if="isSaving" class="spinner-sm me-1"></span>
                   {{ isSaving ? 'Saving...' : (editingEntity ? 'Update Entity' : 'Create Entity') }}
                </button>
                <button v-if="editingEntity" type="button" @click="cancelEdit" class="btn btn-secondary ms-2">Cancel Edit</button>
              </form>
              <!-- END ADDED Entity Form -->
           </div>
        </div>

        <!-- Section for Listing Existing Entities -->
        <div class="entity-list-section">
          <h4>Existing Entities</h4>
          <div v-if="isLoading" class="loading-inline">
            <div class="spinner-sm"></div>
            <span>Loading entities...</span>
          </div>
           <div v-else-if="entities.length === 0" class="empty-state">
              <p>No entities created yet.</p>
            </div>
          <table v-else class="table table-sm table-striped entities-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Email</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <!-- Ensure this <template> tag wraps BOTH <tr> elements -->
              <template v-for="entity in entities" :key="entity.id">
                <!-- First Row: Entity Info -->
                <tr class="entity-row">
                  <td>{{ entity.name }}</td>
                  <td>{{ entity.entity_type }}</td>
                  <td>{{ entity.primary_email || '--' }}</td>
                  <td>
                     <!-- Edit, Delete Buttons -->
                     <button class="btn btn-xs btn-light me-1" @click="startEdit(entity)" title="Edit Entity"><i class="fas fa-edit"></i></button>
                     <button class="btn btn-xs btn-light text-danger me-1" @click="deleteEntity(entity.id)" title="Delete Entity"><i class="fas fa-trash"></i></button>
                  </td>
                </tr>
                <!-- Second Row: Contacts -->
                <tr class="contacts-row">
                  <td colspan="4">
                    <div class="contacts-section p-3 bg-light border rounded">
                      <h5 class="mb-3">Contacts for {{ entity.name }}</h5>

                      <div v-if="loadingContactsFor[entity.id]" class="loading-inline">
                        <span class="spinner-sm me-1"></span> Loading contacts...
                      </div>
                      <div v-else-if="!entityContacts[entity.id] || entityContacts[entity.id].length === 0" class="empty-state-mini">
                        <i class="fas fa-user-slash"></i> No contacts yet — add one below.
                      </div>
                      <ul v-else class="list-unstyled mb-3">
                        <li
                          v-for="contact in entityContacts[entity.id]"
                          :key="contact.id"
                          class="contact-item d-flex justify-content-between align-items-center mb-2 p-2 bg-white border rounded"
                        >
                          <span>
                            <i class="fas fa-user me-1 text-secondary"></i>
                            <strong>{{ contact.name }}</strong>
                            <span v-if="contact.contact_role_display" class="badge badge-role ms-1">{{ contact.contact_role_display }}</span>
                            <span v-if="contact.is_primary" class="badge badge-primary ms-1">Primary</span>
                            <br>
                            <small class="text-muted">
                              {{ contact.email }}
                              <span v-if="contact.phone"> · {{ contact.phone }}</span>
                              <span v-if="contact.title"> · {{ contact.title }}</span>
                            </small>
                          </span>
                          <span>
                            <button
                              class="btn btn-xs btn-link text-danger"
                              title="Delete Contact"
                              @click="deleteContact(contact.id, entity.id)"
                            ><i class="fas fa-trash"></i></button>
                          </span>
                        </li>
                      </ul>

                      <div class="add-contact-section">
                        <button
                          v-if="!showAddContactForm[entity.id]"
                          @click="openAddContactForm(entity.id)"
                          class="btn btn-sm btn-outline-primary mt-1"
                        >
                          <i class="fas fa-plus"></i> Add Contact
                        </button>

                        <form
                          v-if="showAddContactForm[entity.id]"
                          @submit.prevent="addContact(entity.id)"
                          class="mt-3 border p-3 rounded bg-white"
                        >
                          <h6 class="mb-3">New Contact</h6>
                          <div class="row">
                            <div class="col-md-6 form-group mb-2">
                              <label :for="`contact-name-${entity.id}`">Name <span class="required-star">*</span></label>
                              <input
                                type="text"
                                :id="`contact-name-${entity.id}`"
                                v-model="newContactFormData[entity.id].name"
                                class="form-control form-control-sm"
                                required
                              />
                            </div>
                            <div class="col-md-6 form-group mb-2">
                              <label :for="`contact-role-${entity.id}`">Role <span class="required-star">*</span></label>
                              <select
                                :id="`contact-role-${entity.id}`"
                                v-model="newContactFormData[entity.id].contact_role"
                                class="form-control form-control-sm"
                                required
                              >
                                <option value="">-- Select Role --</option>
                                <optgroup label="Store / Franchisee">
                                  <option v-for="r in CONTACT_ROLES.filter(r => r.group === 'Store / Franchisee')" :key="r.value" :value="r.value">{{ r.label }}</option>
                                </optgroup>
                                <optgroup label="Insurance Agency">
                                  <option v-for="r in CONTACT_ROLES.filter(r => r.group === 'Insurance Agency')" :key="r.value" :value="r.value">{{ r.label }}</option>
                                </optgroup>
                                <optgroup label="General">
                                  <option v-for="r in CONTACT_ROLES.filter(r => r.group === 'General')" :key="r.value" :value="r.value">{{ r.label }}</option>
                                </optgroup>
                              </select>
                            </div>
                          </div>
                          <div class="row">
                            <div class="col-md-6 form-group mb-2">
                              <label :for="`contact-email-${entity.id}`">Email <span class="required-star">*</span></label>
                              <input
                                type="email"
                                :id="`contact-email-${entity.id}`"
                                v-model="newContactFormData[entity.id].email"
                                class="form-control form-control-sm"
                                required
                              />
                            </div>
                            <div class="col-md-6 form-group mb-2">
                              <label :for="`contact-phone-${entity.id}`">Phone</label>
                              <input
                                type="tel"
                                :id="`contact-phone-${entity.id}`"
                                v-model="newContactFormData[entity.id].phone"
                                class="form-control form-control-sm"
                                placeholder="(555) 123-4567"
                              />
                            </div>
                          </div>
                          <div class="row">
                            <div class="col-md-6 form-group mb-2">
                              <label :for="`contact-title-${entity.id}`">Job Title <small class="text-muted">(optional)</small></label>
                              <input
                                type="text"
                                :id="`contact-title-${entity.id}`"
                                v-model="newContactFormData[entity.id].title"
                                class="form-control form-control-sm"
                                placeholder="e.g., Regional Manager"
                              />
                            </div>
                            <div class="col-md-6 form-group mb-2 d-flex align-items-end">
                              <div class="form-check">
                                <input
                                  type="checkbox"
                                  :id="`contact-primary-${entity.id}`"
                                  v-model="newContactFormData[entity.id].is_primary"
                                  class="form-check-input"
                                />
                                <label :for="`contact-primary-${entity.id}`" class="form-check-label">Primary contact</label>
                              </div>
                            </div>
                          </div>
                          <div v-if="addContactError[entity.id]" class="error-message mt-2 mb-2">
                            {{ addContactError[entity.id] }}
                          </div>
                          <div>
                            <button
                              type="submit"
                              class="btn btn-sm btn-success"
                              :disabled="isSavingContact[entity.id] || !newContactFormData[entity.id].name || !newContactFormData[entity.id].email || !newContactFormData[entity.id].contact_role"
                            >
                              <span v-if="isSavingContact[entity.id]" class="spinner-sm me-1"></span>
                              Save Contact
                            </button>
                            <button type="button" @click="cancelAddContact(entity.id)" class="btn btn-sm btn-secondary ms-2">Cancel</button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </td>
                </tr>
              </template> <!-- Ensure this closes the v-for template -->
            </tbody>
          </table>
           <!-- TODO: Add pagination if list becomes long -->
        </div>
      </div>

      <div class="modal-footer">
        <button @click="close" class="btn btn-secondary">Close</button>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';
import { CONTACT_ROLES } from '@/constants';
import AddressFields from '@/components/shared/AddressFields.vue';

const initialFormData = () => ({
  name: '',
  entity_type: 'COMPANY',
  primary_email: '',
  primary_phone: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  province: '',
  postal_code: '',
  country: 'US',
});

const initialContactData = () => ({
  name: '',
  email: '',
  phone: '',
  contact_role: '',
  title: '',
  is_primary: false,
});

export default {
  name: 'EntityManagerModal',
  components: { AddressFields },
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    projectId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      error: null,
      entities: [],
      editingEntity: null,
      entityFormData: initialFormData(),
      // Contact state — keyed by entity.id, using plain reactive objects (Vue 3 compatible)
      entityContacts: {},
      loadingContactsFor: {},
      showAddContactForm: {},
      newContactFormData: {},
      isSavingContact: {},
      addContactError: {},
      CONTACT_ROLES,
    };
  },
  watch: {
    show(newValue) {
      if (newValue) {
        this.fetchEntities();
        this.cancelEdit(); 
        this.error = null;
        // Reset all contact states when modal is reshown if desired, or manage individually
        this.visibleContacts = {}; 
        this.entityContacts = {};
        this.loadingContactsFor = {};
        this.showAddContactForm = {};
        this.newContactFormData = {};
        this.isSavingContact = {};
        this.addContactError = {};
      }
    }
  },
  methods: {
    close() {
      this.$emit('close');
    },
    async fetchEntities() {
      this.isLoading = true;
      this.error = null;
      this.entities = [];
      try {
        const response = await api.get('/entities/', {
          params: { project_id: this.projectId }
        });
         if (response && response.data) {
            let fetchedEntities = [];
            if (Array.isArray(response.data)) {
                fetchedEntities = response.data;
            } else if (response.data.results && Array.isArray(response.data.results)) {
                fetchedEntities = response.data.results; // Handle pagination
            } else {
                 console.warn("Unexpected entities structure:", response.data);
            }
            this.entities = fetchedEntities;
            this.entities.forEach(entity => {
              this.entityContacts[entity.id] = [];
              this.loadingContactsFor[entity.id] = false;
              this.showAddContactForm[entity.id] = false;
              this.newContactFormData[entity.id] = initialContactData();
              this.isSavingContact[entity.id] = false;
              this.addContactError[entity.id] = null;
              this.fetchEntityContacts(entity.id);
            });
        } else {
            console.warn("No data received for entities");
        }
      } catch (err) {
        console.error('Error fetching entities:', err);
        this.error = 'Failed to load entities. ' + (err.message || '');
      } finally {
        this.isLoading = false;
      }
    },
    async fetchEntityContacts(entityId) {
      this.loadingContactsFor[entityId] = true;
      try {
        const response = await api.get(`/contacts/`, { params: { entity: entityId } });
        const data = response.data;
        this.entityContacts[entityId] = Array.isArray(data) ? data : (data.results || []);
      } catch (err) {
        console.error(`Error fetching contacts for entity ${entityId}:`, err);
        this.entityContacts[entityId] = [];
      } finally {
        this.loadingContactsFor[entityId] = false;
      }
    },
    startEdit(entity) {
      this.editingEntity = { ...entity }; // Clone entity to avoid modifying original
      // Map entity fields to form data
      this.entityFormData = {
        name: entity.name || '',
        entity_type: entity.entity_type || 'COMPANY',
        primary_email: entity.primary_email || '',
        primary_phone: entity.primary_phone || '',
        address_line1: entity.address_line1 || '',
        address_line2: entity.address_line2 || '',
        city: entity.city || '',
        state: entity.state || '',
        province: entity.province || '',
        postal_code: entity.postal_code || '',
        country: entity.country || 'US',
      };
       console.log("Started editing", this.editingEntity);
       this.error = null; // Clear previous errors
       // Scroll form into view or focus first field (optional UX)
       this.$nextTick(() => {
         const formEl = this.$el.querySelector('.entity-form-section');
         if (formEl) formEl.scrollIntoView({ behavior: 'smooth' });
         const nameInput = this.$el.querySelector('#entity-name');
         if (nameInput) nameInput.focus();
       });
    },
    cancelEdit() {
      this.editingEntity = null;
      this.entityFormData = initialFormData(); // Reset form
      this.error = null; // Clear errors on cancel
    },
    openAddContactForm(entityId) {
      if (!this.newContactFormData[entityId]) {
        this.newContactFormData[entityId] = initialContactData();
      }
      this.showAddContactForm[entityId] = true;
      this.addContactError[entityId] = null;
    },
    cancelAddContact(entityId) {
      this.showAddContactForm[entityId] = false;
      this.addContactError[entityId] = null;
      this.newContactFormData[entityId] = initialContactData();
    },
    async addContact(entityId) {
      this.isSavingContact[entityId] = true;
      this.addContactError[entityId] = null;
      const payload = { ...this.newContactFormData[entityId], entity: entityId };

      try {
        await api.post('/contacts/', payload);
        this.showAddContactForm[entityId] = false;
        this.newContactFormData[entityId] = initialContactData();
        this.fetchEntityContacts(entityId);
      } catch (err) {
        console.error(`Error adding contact for entity ${entityId}:`, err);
        let errorMessage = 'Failed to add contact.';
        if (err.response && err.response.data) {
          if (typeof err.response.data === 'string') {
            errorMessage = err.response.data;
          } else if (err.response.data.detail) {
            errorMessage = err.response.data.detail;
          } else {
            const parts = [];
            for (const field in err.response.data) {
              parts.push(`${field}: ${err.response.data[field].join(', ')}`);
            }
            if (parts.length > 0) errorMessage = parts.join('; ');
          }
        }
        this.addContactError[entityId] = errorMessage;
      } finally {
        this.isSavingContact[entityId] = false;
      }
    },
    async saveEntity() {
      this.isSaving = true;
      this.error = null;
      const payload = { ...this.entityFormData };

      // Clean up empty strings to null if backend expects nulls
      Object.keys(payload).forEach(key => {
          if (payload[key] === '') {
              payload[key] = null;
          }
      });

      // Ensure project ID is included on creation
      if (!this.editingEntity) {
          payload.project = this.projectId;
      }

      try {
        let response;
        if (this.editingEntity) {
          // --- UPDATE (PUT/PATCH) --- TODO: Implement fully
          console.log("Attempting to update entity:", this.editingEntity.id, payload);
          response = await api.put(`/entities/${this.editingEntity.id}/`, payload);
          // Or use PATCH: await api.patch(`/entities/${this.editingEntity.id}/`, payload);
          console.log("Update response:", response);
          this.$emit('entities-updated'); // Notify parent
        } else {
          // --- CREATE (POST) ---
          console.log("Attempting to create entity:", payload);
          response = await api.post('/entities/', payload);
          console.log("Create response:", response);
          this.$emit('entities-updated'); // Notify parent
        }

        // On success (for both create and update)
        this.fetchEntities(); // Refresh the list
        this.cancelEdit(); // Clear form and editing state
        // Optionally show success message

      } catch (err) {
        console.error('Error saving entity:', err);
        // Handle specific errors (e.g., validation, uniqueness)
        if (err.response && err.response.data) {
           if (typeof err.response.data === 'string') {
               this.error = err.response.data;
           } else if (err.response.data.detail) {
               this.error = err.response.data.detail;
           } else {
               // Format DRF validation errors
               let errorMsg = 'Validation Error:';
               for (const field in err.response.data) {
                   errorMsg += ` ${field}: ${err.response.data[field].join(', ')}`;
               }
               this.error = errorMsg;
           }
        } else {
             this.error = 'Failed to save entity. ' + (err.message || '');
        }
      } finally {
        this.isSaving = false;
      }
    },
     async deleteEntity(entityId) { // <-- Make async
        console.log("Attempting to delete entity:", entityId);
        if (confirm('Are you sure you want to delete this entity? This cannot be undone and might affect related records.')) {
           this.error = null; // Clear previous errors
           try {
              await api.delete(`/entities/${entityId}/`);
              console.log("Entity deleted successfully");
              this.fetchEntities(); // Refresh list
              // If the deleted entity was being edited, clear the form
              if (this.editingEntity && this.editingEntity.id === entityId) {
                this.cancelEdit();
              }
              this.$emit('entities-updated'); // Notify parent
           } catch (err) {
              console.error('Error deleting entity:', err);
              this.handleError(err, 'Failed to delete entity.'); // Use mixin or display error
           }
        }
     },
     handleError(error, defaultMessage = 'An error occurred.') {
       if (error && error.response && error.response.data) {
         if (typeof error.response.data === 'string') {
           this.error = error.response.data;
         } else if (error.response.data.detail) {
           this.error = error.response.data.detail;
         } else {
           let errorMsg = 'Validation Error:';
           for (const field in error.response.data) {
             errorMsg += ` ${field}: ${error.response.data[field].join(', ')}`;
           }
           this.error = errorMsg;
         }
       } else {
         this.error = error.message || defaultMessage;
       }
       console.error(this.error);
     },
    async deleteContact(contactId, entityId) {
      if (!confirm('Remove this contact? This cannot be undone.')) return;
      try {
        await api.delete(`/contacts/${contactId}/`);
        this.fetchEntityContacts(entityId);
      } catch (err) {
        console.error('Error deleting contact:', err);
        this.addContactError[entityId] = 'Failed to delete contact.';
      }
    },
  },
};
</script>

<style scoped>
/* Reuse modal styles or add specific ones */
.modal-large {
  min-width: 600px; /* Make modal wider */
  max-width: 80%;
}
.card {
    border: 1px solid #e2e8f0;
    border-radius: .375rem;
    background-color: #fff;
    margin-bottom: 1rem;
}
.card-header {
    padding: .75rem 1.25rem;
    margin-bottom: 0;
    background-color: #f7fafc;
    border-bottom: 1px solid #e2e8f0;
    display: flex;
    justify-content: space-between;
    align-items: center;
}
.card-header h4 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
}
.card-body {
    padding: 1.25rem;
}
.table {
    width: 100%;
    margin-bottom: 1rem;
    color: #212529;
    vertical-align: top;
    border-color: #dee2e6;
    border-collapse: collapse;
}
.table-sm > :not(caption) > * > * {
    padding: .25rem .25rem;
}

/* Modal positioning fixes */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start; /* Changed from center to flex-start */
  justify-content: center;
  z-index: 1000;
  padding: 20px 0; /* Add padding to ensure modal doesn't touch edges */
  overflow-y: auto; /* Allow scrolling if modal is taller than viewport */
}

.modal-container {
  background-color: white;
  border-radius: 8px;
  width: 800px; /* modal-large size */
  max-width: 90%;
  max-height: calc(100vh - 40px); /* Ensure modal doesn't exceed viewport minus padding */
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-top: 20px; /* Add some top margin to prevent it from being at the very top */
  margin-bottom: 20px; /* Add bottom margin for consistency */
}

.modal-header {
  padding: 15px 20px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0; /* Prevent header from shrinking */
}

.modal-header h3 {
  margin: 0;
  font-size: 20px;
}

.modal-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #757575;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-close:hover {
  color: #333;
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex-grow: 1;
  flex-shrink: 1; /* Allow body to shrink if needed */
  min-height: 0; /* Important for flex child to be able to shrink */
}

.table-striped > tbody > tr:nth-of-type(odd) {
    background-color: rgba(0,0,0,.05);
}
.table thead th {
    vertical-align: bottom;
    border-bottom: 2px solid #dee2e6;
    text-align: left;
    padding: .5rem .5rem;
}
.table tbody td {
    vertical-align: middle;
     padding: .5rem .5rem;
     border-bottom: 1px solid #dee2e6;
}

.error-message {
    color: #dc3545;
    background-color: #f8d7da;
    border: 1px solid #f5c2c7;
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 0.9em;
}
.mb-3 {
  margin-bottom: 1rem !important;
}
.mb-4 {
  margin-bottom: 1.5rem !important;
}
.ms-2 {
  margin-left: 0.5rem !important;
}
.me-1 {
  margin-right: 0.25rem !important;
}
.btn-xs {
    padding: .1rem .3rem;
    font-size: .75rem;
    border-radius: .2rem;
}
.btn-light {
    color: #000;
    background-color: #f8f9fa;
    border-color: #f8f9fa;
}
.btn-light:hover {
    color: #000;
    background-color: #f9fafb;
    border-color: #f9fafb;
}
.text-danger {
  color: #dc3545 !important;
}
.text-danger:hover {
  color: #bb2d3b !important;
}

/* Basic spinner */
.spinner-sm {
  display: inline-block;
  width: 1em;
  height: 1em;
  vertical-align: -0.125em;
  border: 0.2em solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: spinner-border .75s linear infinite;
}
@keyframes spinner-border { to { transform: rotate(360deg); } }

.loading-inline, .empty-state {
  text-align: center;
  padding: 20px;
  color: #6c757d;
}

.empty-state-mini {
  padding: 10px 0;
  color: #6c757d;
  font-size: 0.9rem;
}

.badge-role {
  background-color: #6610f2;
  color: white;
  font-size: 0.7rem;
  padding: 3px 7px;
  border-radius: 4px;
}

.badge-primary {
  background-color: #0d6efd;
  color: white;
  font-size: 0.7rem;
  padding: 3px 7px;
  border-radius: 4px;
}

.required-star {
  color: #dc3545;
  margin-left: 2px;
}

.contact-item {
  transition: background-color 0.15s;
}

.form-check {
  padding-left: 1.5em;
}

.form-check-input {
  margin-left: -1.5em;
}

.d-flex { display: flex !important; }
.align-items-end { align-items: flex-end !important; }
.col-md-5 { flex: 0 0 41.666667%; max-width: 41.666667%; padding: 0 10px; }
</style>
